import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenant } from "@/core/tenant/TenantProvider";
import { Alert, Button, Card, CardTitle, Field, Input } from "@/core/ui";
import { startSsoLogin, useLocalLogin, useSession } from "./useSession";

export function LoginPage() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const session = useSession();
  const login = useLocalLogin();
  const [params] = useSearchParams();
  const location = useLocation();
  const next = (location.state as { from?: string } | null)?.from ?? params.get("next") ?? "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showLocal, setShowLocal] = useState(!tenant.sso_enabled);

  if (session.data?.authenticated) return <Navigate to={next} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password });
  };

  return (
    <div className="bt-login">
      <Card>
        <CardTitle>{t("auth.sign_in_to", { name: tenant.branding.display_name })}</CardTitle>
        {params.get("error") === "sso" ? <Alert tone="danger">{t("auth.sso_error")}</Alert> : null}
        {tenant.sso_enabled ? (
          <Button
            className="bt-login__sso"
            size="lg"
            onClick={() => startSsoLogin(session.data?.sso_provider_id ?? "tenant-oidc", next)}
          >
            {t("auth.sign_in_sso")}
          </Button>
        ) : null}
        {tenant.sso_enabled && !showLocal ? (
          <button
            type="button"
            className="bt-login__local-toggle"
            onClick={() => setShowLocal(true)}
          >
            {t("auth.local_admin_link")}
          </button>
        ) : null}
        {showLocal ? (
          <form onSubmit={submit} className="bt-login__form">
            <Field label={t("auth.email")} htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label={t("auth.password")} htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {login.isError ? <Alert tone="danger">{t("auth.invalid_credentials")}</Alert> : null}
            <Button type="submit" className="bt-login__submit" loading={login.isPending}>
              {t("auth.sign_in_local")}
            </Button>
            <p className="bt-login__hint">{t("auth.local_admin_hint")}</p>
          </form>
        ) : null}
      </Card>
    </div>
  );
}
